import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { Message } from '@app/app/_components/Message/Message';
import { RangeSlider } from '@app/app/_components/RangeSlider/RangeSlider';
import { RangeSliderUnit } from '@app/domain/model/RangeSliderUnit';
import { SessionResponse } from '@app/domain/model/eshop/life/session/SessionResponse';
import { calculateAgeFromDateOfBirth } from '@app/service/eshop/life/utils/calculateAgeFromDateOfBirth';
import {
    FALLBACK_INSURANCE_RANGE_MAX_VALUE,
    INSURANCE_RANGE_MAX_VALUE_THRESHOLD,
} from '@app/app/esales/life/_sections/LifeInsuranceWizardSection/helpers/businessValueConfig';
import { useDebouncedCallback } from 'use-debounce';

interface Props {
    dateOfBirth: Nullable<string>;
    sessionData: Nullable<SessionResponse>;
    currentValue?: Nullable<number>;
    onRangeChange: () => void;
}

export function InsuranceDurationStep({ dateOfBirth, sessionData, currentValue, onRangeChange }: Props) {
    const t = useTranslations();

    const age = useMemo(() => (dateOfBirth ? calculateAgeFromDateOfBirth(dateOfBirth) : null), [dateOfBirth]);

    const maxYears = useMemo(
        () => Math.max(1, age ? INSURANCE_RANGE_MAX_VALUE_THRESHOLD - age : FALLBACK_INSURANCE_RANGE_MAX_VALUE),
        [age],
    );

    const debouncedChangeYears = useDebouncedCallback(() => {
        void onRangeChange();
    }, 500);

    const defaultDuration = useMemo(() => {
        let raw: number;

        switch (true) {
            case !!currentValue:
                raw = currentValue;
                break;
            case !!sessionData?.firstStep?.insuranceDurationYears:
                raw = sessionData.firstStep.insuranceDurationYears;
                break;
            case !age:
                raw = 20;
                break;
            default:
                raw = age! >= 50 ? Math.max(1, INSURANCE_RANGE_MAX_VALUE_THRESHOLD - age!) : 20;
        }

        return Math.min(raw, maxYears);
    }, [maxYears, currentValue, sessionData, age]);

    return (
        <div className="flex flex-col gap-4">
            <RangeSlider
                key={age}
                unit={RangeSliderUnit.Years}
                min={1}
                defaultValue={defaultDuration}
                max={maxYears}
                name="insuranceDurationYears"
                onChange={debouncedChangeYears}
            />
            <Message variant="info">
                {t.rich('eshop.life.forHowManyYearsInsuranceDescription', {
                    maxYears,
                })}
            </Message>
        </div>
    );
}
